// Opening a video inside research (busy-ai-srv#14).
//
// Until now a video row opened as a poster, a title and a "Watch" link that took the user off Busy —
// the one result kind that could not be read where it was found. Opening one now REPLACES the row of
// choices with that video: the player where the host allows one, and a panel under it that says what
// the video is about before twelve minutes are spent on it.
//
// Everything here is pure — the embed decision, the panel's fields, and what is sent to the brief
// endpoint — so the rules are readable (and testable) without mounting the viewer or calling an API.

/**
 * The player src for a video, or null when the host will not be framed.
 *
 * The allow-list is deliberate and short: these are the hosts that PUBLISH an embed player (the
 * same set their oEmbed endpoints hand out), so framing them is the documented way to play them.
 * Everything else — a news site's own player, a shop's product video, a random CDN mp4 — refuses
 * the frame with X-Frame-Options and shows the user a grey box, so it never gets one: those keep
 * the poster and the "Watch" button, which is a working answer rather than a broken player.
 *
 * `?feature=oembed` is not added and no host is contacted: the id is read out of the URL we already
 * have, which is why this is a pure function and not a fetch.
 */
export function videoEmbedUrl (url) {
  const raw = String(url ?? '').trim()
  if (!raw) return null

  let parsed
  try { parsed = new URL(raw) } catch { return null }
  // http:// pages are blocked on an https app anyway, and an embed is the one place a mixed-content
  // frame fails silently rather than visibly.
  if (parsed.protocol !== 'https:') return null

  const host = parsed.hostname.replace(/^www\./i, '').toLowerCase()

  if (host === 'youtu.be') {
    const id = parsed.pathname.split('/').filter(Boolean)[0]
    return id ? `https://www.youtube.com/embed/${id}` : null
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const id = parsed.searchParams.get('v')
      || (parsed.pathname.match(/^\/(?:embed|shorts|v)\/([^/?#]+)/) || [])[1]
    return id ? `https://www.youtube.com/embed/${id}` : null
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = (parsed.pathname.match(/(\d{6,})/) || [])[1]
    return id ? `https://player.vimeo.com/video/${id}` : null
  }
  if (host === 'dailymotion.com' || host === 'dai.ly') {
    const id = host === 'dai.ly'
      ? parsed.pathname.split('/').filter(Boolean)[0]
      : (parsed.pathname.match(/^\/video\/([^/?#]+)/) || [])[1]
    return id ? `https://www.dailymotion.com/embed/video/${id}` : null
  }
  return null
}

/** What `POST v1/ai/video-brief` is asked for — only fields the result row already carries. */
export function videoBriefPayload (item) {
  if (!item?.url) return null
  return {
    url: item.url,
    title: item.title || '',
    // The row's snippet IS the description the video engine returned; a saved video keeps it in
    // `content`, which is the field every persisted research item writes its text into.
    description: item.snippet || item.content || '',
    author: item.author || '',
    length: item.length || '',
  }
}

/**
 * The panel under the video: what we know from the row (always), plus what the brief added (when it
 * answered). Never throws and never leaves the panel empty-handed — a video with no brief still
 * shows its title, channel and duration, which is strictly more than the old "poster + Watch".
 *
 * `pending` is the honest third state: the request is in flight, so the panel says it is reading
 * rather than claiming there is nothing to say.
 */
export function videoPanel (item, brief, { pending = false } = {}) {
  const summary = String(brief?.summary ?? '').trim()
  return {
    title: (item?.title || '').trim(),
    channel: (item?.author || '').trim(),
    duration: (item?.length || '').trim(),
    published: (item?.publishedDate || '').trim(),
    summary,
    keyPoints: list(brief?.keyPoints, 5),
    relatedQuestions: list(brief?.relatedQuestions, 3),
    pending: !!pending && !summary,
    // True when the panel has nothing beyond the row's own metadata — the viewer then says so
    // instead of drawing an empty block under the player.
    empty: !pending && !summary && list(brief?.keyPoints, 5).length === 0,
  }
}

function list (values, max) {
  return (Array.isArray(values) ? values : [])
    .map(v => String(v ?? '').trim())
    .filter(Boolean)
    .slice(0, max)
}
